// frontend/src/api/tickets.js
/**
 * Support-ticket API — thin wrappers around the /api/tickets endpoints (P16-G10).
 */
import client from "./client";

/**
 * List the org's support tickets, optionally filtered by status.
 * @param {{ status?: string, page?: number, perPage?: number }} [params]
 * @returns {Promise<{items: Array, page: number, per_page: number, total: number}>}
 */
export async function listTickets({ status = null, page = 1, perPage = 50 } = {}) {
  const params = { page, per_page: perPage };
  if (status) params.status = status;
  const { data } = await client.get("/api/tickets", { params });
  return data;
}

/**
 * Create a support ticket.
 * @param {{ subject: string, description: string, category: string, priority?: string }} payload
 * @returns {Promise<object>}  the created ticket
 */
export async function createTicket(payload) {
  const { data } = await client.post("/api/tickets", payload);
  return data;
}

/**
 * Fetch a single ticket by id.
 * @param {string} ticketId
 * @returns {Promise<object>}
 */
export async function getTicket(ticketId) {
  const { data } = await client.get(`/api/tickets/${ticketId}`);
  return data;
}

/**
 * Update a ticket's status (admin+).
 * @param {string} ticketId
 * @param {string} status  open | in_progress | resolved | closed
 * @returns {Promise<object>}
 */
export async function updateTicketStatus(ticketId, status) {
  const { data } = await client.patch(`/api/tickets/${ticketId}/status`, { status });
  return data;
}

/**
 * Post a reply to a ticket's conversation thread (M4a).
 * @param {string} ticketId
 * @param {string} body
 * @returns {Promise<object>}  the created message
 */
export async function addTicketMessage(ticketId, body) {
  const { data } = await client.post(`/api/tickets/${ticketId}/messages`, { body });
  return data;
}

/**
 * Resolve your own ticket, optionally rating the answer (requester self-close).
 * @param {string} ticketId
 * @param {"positive"|"negative"|null} [satisfaction]
 * @returns {Promise<object>}  the updated ticket
 */
export async function resolveTicket(ticketId, satisfaction = null) {
  const body = satisfaction ? { satisfaction } : {};
  const { data } = await client.post(`/api/tickets/${ticketId}/resolve`, body);
  return data;
}

/**
 * Reopen a resolved/closed ticket (requester or admin).
 * @param {string} ticketId
 * @returns {Promise<object>}  the updated ticket
 */
export async function reopenTicket(ticketId) {
  const { data } = await client.post(`/api/tickets/${ticketId}/reopen`);
  return data;
}
